export type PayPhoneLanguage = 'es' | 'en';

const languageStorageKey = 'tbc.payphone.language';

export const getInitialPayPhoneLanguage = (): PayPhoneLanguage => {
    if (typeof window === 'undefined') {
        return 'es';
    }

    const queryLanguage = new URLSearchParams(window.location.search).get('lang')?.toLowerCase();
    if (queryLanguage === 'en' || queryLanguage === 'es') {
        return queryLanguage;
    }

    const storedLanguage = window.localStorage.getItem(languageStorageKey);
    if (storedLanguage === 'en' || storedLanguage === 'es') {
        return storedLanguage;
    }

    return window.navigator.language.toLowerCase().startsWith('en') ? 'en' : 'es';
};

export const persistPayPhoneLanguage = (language: PayPhoneLanguage) => {
    window.localStorage.setItem(languageStorageKey, language);
};

export const payPhoneLocale = (language: PayPhoneLanguage) => language === 'en' ? 'en-US' : 'es-EC';

export const payPhoneResponseText = {
    es: {
        pending: 'Pendiente',
        notRegistered: 'No registrado',
        confirmingTitle: 'Confirmando tu pago',
        confirmingText: 'Estamos validando la transaccion con PayPhone y preparando tu cuenta.',
        missingInfo: 'PayPhone no retorno la informacion necesaria para confirmar el pago.',
        transactionMismatch: 'La transaccion retornada por PayPhone no coincide con el pago generado en el portal.',
        confirmError: 'No se pudo confirmar el pago con PayPhone.',
        errorTitle: 'No se pudo confirmar el pago',
        notApproved: 'PayPhone no aprobo la transaccion.',
        back: 'Volver',
        retry: 'Volver a intentar',
        voucherTitle: 'Confirmacion de pago',
        approved: 'Aprobado',
        completed: 'Pago completado',
        hello: 'Hola',
        successText: 'La tarjeta fue validada correctamente. El cargo temporal se reverso y tu periodo de prueba quedo activo.',
        validationTitle: 'Validacion cobrada',
        validationSubtitle: 'Cargo temporal PayPhone',
        date: 'Fecha',
        status: 'Estado',
        transaction: 'Transaccion',
        authorization: 'Autorizacion',
        reverseTitle: 'Reverso del cargo',
        reverseSubtitle: 'El valor se devuelve luego de validar la tarjeta',
        reverseDate: 'Fecha de reverso',
        reversedAmount: 'Monto reversado',
        reverseTransaction: 'Transaccion de reverso',
        tokenization: 'Tokenizacion',
        reversed: 'REVERSADO',
        reversePending: 'PENDIENTE',
        organization: 'Organizacion',
        trialContract: 'Contrato de prueba',
        generated: 'Generado',
        notGenerated: 'No generado',
        cardHolder: 'Titular',
        document: 'Documento',
        card: 'Tarjeta',
        reference: 'Referencia',
        tokenSaved: 'Token guardado',
        goPortal: 'Ir a mi consultorio',
        footer: 'Gracias por elegir BlueMedix. Tu cuenta esta lista para comenzar.',
        protected: 'Pago protegido por PayPhone'
    },
    en: {
        pending: 'Pending',
        notRegistered: 'Not registered',
        confirmingTitle: 'Confirming your payment',
        confirmingText: 'We are validating the PayPhone transaction and preparing your account.',
        missingInfo: 'PayPhone did not return the information required to confirm the payment.',
        transactionMismatch: 'The transaction returned by PayPhone does not match the payment generated in the portal.',
        confirmError: 'We could not confirm the payment with PayPhone.',
        errorTitle: 'We could not confirm the payment',
        notApproved: 'PayPhone did not approve the transaction.',
        back: 'Back',
        retry: 'Try again',
        voucherTitle: 'Payment confirmation',
        approved: 'Approved',
        completed: 'Payment completed',
        hello: 'Hello',
        successText: 'Your card was validated successfully. The temporary charge was reversed and your trial period is active.',
        validationTitle: 'Validation charged',
        validationSubtitle: 'Temporary PayPhone charge',
        date: 'Date',
        status: 'Status',
        transaction: 'Transaction',
        authorization: 'Authorization',
        reverseTitle: 'Charge reversal',
        reverseSubtitle: 'The amount is returned after validating the card',
        reverseDate: 'Reversal date',
        reversedAmount: 'Reversed amount',
        reverseTransaction: 'Reversal transaction',
        tokenization: 'Tokenization',
        reversed: 'REVERSED',
        reversePending: 'PENDING',
        organization: 'Organization',
        trialContract: 'Trial contract',
        generated: 'Generated',
        notGenerated: 'Not generated',
        cardHolder: 'Cardholder',
        document: 'Document',
        card: 'Card',
        reference: 'Reference',
        tokenSaved: 'Token saved',
        goPortal: 'Go to my clinic',
        footer: 'Thanks for choosing BlueMedix. Your account is ready to start.',
        protected: 'Payment protected by PayPhone'
    }
};

export const payPhoneBridgeText = {
    es: {
        missingToken: 'El enlace de pago no tiene la informacion necesaria.',
        linkError: 'No se pudo generar el enlace seguro de PayPhone.',
        prepareError: 'No se pudo preparar el pago con PayPhone.',
        errorTitle: 'No se pudo abrir PayPhone',
        retry: 'Volver a intentar',
        title: 'Preparando pago seguro',
        subtitle: 'Estamos generando el enlace protegido de PayPhone para validar tu tarjeta.',
        stepOneTitle: 'Validando enlace',
        stepOneText: 'Revisamos la organizacion, el plan y la vigencia del pago.',
        stepTwoTitle: 'Redirigiendo a PayPhone',
        stepTwoText: 'La pasarela se abrira en esta misma pestana.',
        alertTitle: 'Tu pago se procesa fuera de BlueMedix.',
        alertText: 'Cuando PayPhone confirme la transaccion, regresaras automaticamente a esta pantalla para activar tu cuenta.',
        continue: 'Continuar a PayPhone'
    },
    en: {
        missingToken: 'The payment link does not include the required information.',
        linkError: 'We could not generate the secure PayPhone link.',
        prepareError: 'We could not prepare the PayPhone payment.',
        errorTitle: 'We could not open PayPhone',
        retry: 'Try again',
        title: 'Preparing secure payment',
        subtitle: 'We are generating the protected PayPhone link to validate your card.',
        stepOneTitle: 'Validating link',
        stepOneText: 'We are checking the organization, plan, and payment validity.',
        stepTwoTitle: 'Redirecting to PayPhone',
        stepTwoText: 'The payment gateway will open in this same tab.',
        alertTitle: 'Your payment is processed outside BlueMedix.',
        alertText: 'When PayPhone confirms the transaction, you will return automatically to this screen to activate your account.',
        continue: 'Continue to PayPhone'
    }
};
